import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { authApi, setSession } from '../api/client.js';

// 演示账号矩阵（与 server/scripts/seedDemoUsers.ts 预置的一致；admin 由 AuthService 启动时自动建）
const DEMO_ACCOUNTS: Array<{
  username: string; password: string; role: string; icon: string; scope: string; accent: string;
}> = [
  { username: 'admin', password: 'Admin@123456', role: '管理员', icon: '🛡️', scope: '全权：含结账/反结账/年结', accent: 'bg-brand-50 text-brand-700 ring-brand-200' },
  { username: 'senior01', password: 'Demo@123456', role: '主管会计', icon: '📐', scope: '科目维护·过账/审核/冲销·信号处理', accent: 'bg-violet-50 text-violet-700 ring-violet-200' },
  { username: 'acc01', password: 'Demo@123456', role: '会计', icon: '✍️', scope: '凭证录入·导入·微信确认·消息摄入', accent: 'bg-teal-50 text-teal-700 ring-teal-200' },
  { username: 'view01', password: 'Demo@123456', role: '浏览员', icon: '👀', scope: '全只读（报表/凭证可看）', accent: 'bg-slate-100 text-slate-600 ring-slate-200' },
];

export default function Login() {
  const navigate = useNavigate();
  const [username, setUsername] = useState('admin');
  const [password, setPassword] = useState('');
  const [showPwd, setShowPwd] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [picked, setPicked] = useState('admin');

  const submit = async (e?: React.FormEvent, creds?: { u: string; p: string }) => {
    e?.preventDefault();
    setError('');
    const u = (creds?.u ?? username).trim();
    const p = (creds?.p ?? password).trim();
    if (!u || !p) {
      setError('请输入用户名和密码');
      return;
    }
    setLoading(true);
    try {
      const res = await authApi.login(u, p);
      setSession(res.token, res.user);
      navigate('/', { replace: true });
    } catch (err: any) {
      setError(err.message || '登录失败');
    } finally {
      setLoading(false);
    }
  };

  // 一键登录：填入该角色凭据并直接提交（试点评审现场分权演示用）
  const quickLogin = (acc: (typeof DEMO_ACCOUNTS)[number]) => {
    setUsername(acc.username);
    setPassword(acc.password);
    setPicked(acc.username);
    void submit(undefined, { u: acc.username, p: acc.password });
  };

  return (
    <div className="min-h-screen lg:grid lg:grid-cols-2 flex items-center justify-center p-4">
      {/* 品牌展示面 */}
      <div className="relative hidden lg:flex flex-col justify-between overflow-hidden bg-brand-gradient text-white p-12 rounded-r-[2.5rem]">
        <div className="absolute -left-10 top-10 w-72 h-72 rounded-full bg-white/10 blur-3xl" />
        <div className="absolute right-0 bottom-0 w-96 h-96 rounded-full bg-violet-400/20 blur-3xl" />
        <div className="relative flex items-center gap-3">
          <div className="w-11 h-11 rounded-2xl bg-white/15 backdrop-blur grid place-items-center text-xl font-black shadow-inset">Z</div>
          <div>
            <p className="font-bold text-lg tracking-wide">ZInternal</p>
            <p className="text-xs text-white/70">AI 智能内账系统</p>
          </div>
        </div>
        <div className="relative">
          <h1 className="text-4xl font-bold leading-tight tracking-tight">让每一笔流水<br />都自动成为合规凭证</h1>
          <p className="text-white/75 mt-4 max-w-md">智能导入·AI 分类·自动记账·经营看板·企微监管，一个后台全部掌握。</p>
          <div className="flex flex-wrap gap-2 mt-8">
            {['智能导入', 'AI 双引擎', '自动凭证', '经营看板', '期末结账'].map((t) => (
              <span key={t} className="badge bg-white/12 ring-1 ring-white/20 text-white">{t}</span>
            ))}
          </div>
        </div>
        <p className="relative text-xs text-white/60">试点版 v0.1 · 智寰科技 × 慧算账</p>
      </div>

      {/* 登录表单 */}
      <div className="w-full max-w-md mx-auto">
        <div className="card p-8 sm:p-10 shadow-card-hover animate-fade-in-up">
          <div className="lg:hidden flex items-center gap-2 mb-6">
            <div className="w-9 h-9 rounded-xl bg-brand-gradient grid place-items-center text-white font-black shadow-glow-sm">Z</div>
            <span className="font-bold text-slate-800">ZInternal</span>
          </div>
          <div className="mb-8">
            <h2 className="text-2xl font-bold text-slate-900">欢迎回来 👋</h2>
            <p className="page-subtitle">登录以继续管理您的内账</p>
          </div>

          <form onSubmit={submit} className="space-y-5">
            <div>
              <label className="label">用户名</label>
              <input
                value={username}
                onChange={(e) => { setUsername(e.target.value); setPicked(''); }}
                className="input"
                placeholder="admin"
              />
            </div>
            <div>
              <label className="label">密码</label>
              <div className="relative">
                <input
                  type={showPwd ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="input pr-11"
                  placeholder="默认密码 Admin@123456"
                  autoComplete="current-password"
                />
                <button
                  type="button"
                  onClick={() => setShowPwd((v) => !v)}
                  className="absolute right-2 top-1/2 -translate-y-1/2 w-8 h-8 grid place-items-center rounded-lg text-slate-400 hover:text-brand-600 hover:bg-brand-50 transition-colors"
                  title={showPwd ? '隐藏密码' : '显示密码'}
                >
                  {showPwd ? '🙈' : '👁️'}
                </button>
              </div>
            </div>

            {error && (
              <p className="text-sm text-red-600 bg-red-50 border border-red-100 rounded-xl px-3 py-2">{error}</p>
            )}

            <button
              type="submit"
              disabled={loading || !username.trim() || !password.trim()}
              className="btn btn-primary w-full"
            >
              {loading ? '登录中…' : '登 录'}
            </button>
          </form>

          <p className="text-xs text-slate-400 text-center mt-6">
            演示账号点击即登录（需先执行 `npm run seed:users` 预置三个业务角色）；登录后请及时修改密码
          </p>

          {/* 演示账号矩阵：四角色一键填充并登录 */}
          <div className="mt-5 pt-5 border-t border-slate-100">
            <p className="text-[11px] font-semibold tracking-wider text-slate-400 mb-2">🎭 角色体验账号</p>
            <div className="grid grid-cols-2 gap-2">
              {DEMO_ACCOUNTS.map((acc) => {
                const active = picked === acc.username;
                return (
                  <button
                    key={acc.username}
                    type="button"
                    onClick={() => quickLogin(acc)}
                    disabled={loading}
                    title={`${acc.username} / ${acc.password} —— ${acc.scope}`}
                    className={`text-left p-3 rounded-xl ring-1 transition-all hover:shadow-md hover:-translate-y-0.5 disabled:opacity-50 disabled:hover:translate-y-0 ${
                      active ? 'ring-2 ring-brand-400 bg-brand-50/60' : 'ring-slate-200 bg-white'
                    }`}
                  >
                    <div className="flex items-center gap-1.5">
                      <span className="text-base leading-none">{acc.icon}</span>
                      <span className="text-sm font-semibold text-slate-800">{acc.role}</span>
                    </div>
                    <span className={`inline-block badge mt-1.5 ring-1 ${acc.accent}`}>{acc.username}</span>
                    <p className="text-[11px] text-slate-500 leading-snug mt-1.5 line-clamp-2">{acc.scope}</p>
                  </button>
                );
              })}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
